// ─────────────────────────────────────────────────────────────────────────────
// REQ-097 — Guard por rol/permiso para el módulo de Reconocimiento.
//
// Lee el rol efectivo del contexto existente (MSAL `useCurrentUser` cuando hay sesión real,
// con fallback al `demoUser` del UserContext, exactamente como el Sidebar). No introduce un
// modelo de auth paralelo. Debe montarse dentro de <UserProvider>.
// ─────────────────────────────────────────────────────────────────────────────

import type { ReactNode } from 'react';
import { useCurrentUser, type UserRole } from '@/hooks/useCurrentUser';
import { useUserContext } from '@/contexts/UserContext';

/**
 * Devuelve el rol efectivo actual (MSAL > demoUser).
 *
 * ⚠️ Es `primaryRole`: el rol de mayor privilegio, pensado ÚNICAMENTE para elegir la ETIQUETA que
 * se muestra en la UI (ver `CurrentUser.primaryRole` en `hooks/useCurrentUser.ts`). **No lo uses
 * para decidir accesos**: el modelo de usuario es multi-rol y un usuario con
 * `['Employee','AdminBrillamos']` se evaluaría solo por el primario. Para autorizar, usa
 * `useEffectiveRoles()`.
 */
export function useEffectiveRole(): UserRole {
  const currentUser = useCurrentUser();
  const { demoUser } = useUserContext();
  return currentUser?.primaryRole ?? demoUser.role;
}

/**
 * Lista COMPLETA de roles efectivos del usuario (MSAL > demoUser). Es la que deben consultar los
 * predicados de autorización: `primaryRole` colapsa el multi-rol a uno solo y esconde permisos.
 */
export function useEffectiveRoles(): UserRole[] {
  const currentUser = useCurrentUser();
  const { demoUser } = useUserContext();
  if (currentUser && currentUser.roles.length > 0) return currentUser.roles;
  return [demoUser.role];
}

/**
 * ¿El usuario tiene este permiso canónico del módulo Reconocimiento? La clave es la de
 * `PermissionKeys` en SharedContracts (`'viewAnalytics'`, `'viewReports'`…), **la misma cadena que
 * evalúa el backend**: comparar contra una etiqueta bonita en vez de la clave canónica es como los
 * puntos acabaron acreditando 0.
 *
 * `null` mientras el perfil se resuelve ⇒ `false`. Es fallo cerrado, y no parpadea porque el guard
 * espera a `profileResolved` antes de decidir.
 */
export function useHasRecognitionPermission(permission: string | undefined): boolean {
  const currentUser = useCurrentUser();
  if (!permission) return false;
  return currentUser?.recognitionPermissions?.includes(permission) ?? false;
}

interface RoleGuardProps {
  /** Roles autorizados a ver el contenido. */
  allow: UserRole[];
  /**
   * Permiso canónico de Reconocimiento que TAMBIÉN abre la puerta, además de los roles de `allow`.
   *
   * Existe porque el backend de varias capacidades —`ModuloMetricasEquipo`,
   * `TableroMetricasLideres`— está declarado con `NoRoleRestriction`: autoriza por permiso y
   * alcance, sin mirar el rol. Gatear aquí solo por rol dejaba a un jefe no-admin **fuera de las
   * métricas de su propio equipo** aunque el servidor se las hubiera servido. Es el defecto que ya
   * se pagó con el 403 de Documentación: el rol no son los permisos.
   *
   * Se SUMA a `allow`, no lo sustituye: nadie que hoy entre deja de entrar.
   */
  orPermission?: string;
  children: ReactNode;
  /** Contenido a mostrar cuando el rol no está autorizado (por defecto: nada). */
  fallback?: ReactNode;
}

/**
 * Renderiza `children` solo si el rol efectivo está en `allow`; de lo contrario `fallback`.
 *
 * Pendiente (REQ-097): cuando se habilite auth real por permisos finos (claims/roles del token del
 * RecognitionService), extender este guard para leer también permisos (`canDelegate`,
 * `canModerate`, …) además del rol. Hoy el rol viene de UserAdminService vía useCurrentUser.
 */
export function RoleGuard({ allow, orPermission, children, fallback = null }: RoleGuardProps) {
  const roles = useEffectiveRoles();
  const currentUser = useCurrentUser();
  const tienePermiso = useHasRecognitionPermission(orPermission);

  if (allow.some((role) => roles.includes(role))) return <>{children}</>;
  if (!orPermission) return <>{fallback}</>;

  // Con el perfil a medio cargar, `recognitionPermissions` es null y decidir ahora enseñaría un
  // «no tienes acceso» que se desmiente medio segundo después. Se espera: fallo cerrado sin mentir.
  if (currentUser && !currentUser.profileResolved) return null;

  return tienePermiso ? <>{children}</> : <>{fallback}</>;
}
